import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  ReputationGenerationRunStatus,
  type ReputationGenerationStatusResponse,
} from '@reach/shared-types/automation-template';
import { useToast } from '../../../../hooks/useToast';
import { templateKeys } from '../../template-query-keys';
import {
  addReputationDefaults,
  getReputationDefaults,
  getReputationGenerationStatus,
  type AddReputationDefaultsRequest,
} from './reputation-defaults-api';

const reputationDefaultsKey = ['partner', 'automation-templates', 'reputation-defaults'] as const;
const generationStatusKey = (jobId: string) => [...reputationDefaultsKey, 'generation', jobId] as const;

const GENERATION_POLL_INTERVAL_MS = 2000;
/** Past this, a run that still says PROCESSING is treated as lost rather than polled forever. */
export const GENERATION_POLL_DEADLINE_MS = 20 * 60 * 1000;

const isPastDeadline = (startedAt: number | undefined) =>
  startedAt !== undefined && Date.now() - startedAt > GENERATION_POLL_DEADLINE_MS;

const hasFinished = (run: ReputationGenerationStatusResponse | undefined) =>
  !!run && run.status !== ReputationGenerationRunStatus.PROCESSING;

export function useReputationDefaults() {
  return useQuery({
    queryKey: reputationDefaultsKey,
    queryFn: getReputationDefaults,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useAddReputationDefaults() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (request: AddReputationDefaultsRequest) => addReputationDefaults(request),
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: reputationDefaultsKey });

      const nothingWasAdded = result.createdCount === 0;
      const generationIsEnqueued = !!result.generationJobId;
      if (generationIsEnqueued && !nothingWasAdded) {
        return;
      }

      addToast({
        title: nothingWasAdded ? 'Already added' : 'Automations added',
        description: nothingWasAdded
          ? 'Those automations are already on your account.'
          : `Added ${result.createdCount} automation(s) as drafts. Choose which tenants get them from each automation.`,
        variant: 'default',
      });
    },
    onError: (error: Error) => {
      addToast({
        title: 'Error',
        description: error.message || 'Failed to add the default automations. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Poll a generation run. Progress is driven entirely by real job state — with imagery a run
 * takes materially longer, so there is no duration to estimate against.
 */
export function useReputationGenerationStatus(
  jobId: string | undefined,
  options: { startedAt?: number } = {},
) {
  const queryClient = useQueryClient();
  const { startedAt } = options;
  const [deadlinePassed, setDeadlinePassed] = useState(() => isPastDeadline(startedAt));

  useEffect(() => {
    setDeadlinePassed(isPastDeadline(startedAt));
    if (startedAt === undefined || isPastDeadline(startedAt)) {
      return;
    }
    const timer = setTimeout(
      () => setDeadlinePassed(true),
      startedAt + GENERATION_POLL_DEADLINE_MS - Date.now(),
    );
    return () => clearTimeout(timer);
  }, [startedAt]);

  const query = useQuery({
    queryKey: generationStatusKey(jobId ?? ''),
    queryFn: () => getReputationGenerationStatus(jobId!),
    enabled: !!jobId,
    staleTime: 0,
    // A transient error must not end polling: the run is still going on the server.
    refetchInterval: q =>
      hasFinished(q.state.data) || deadlinePassed ? false : GENERATION_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const runFinished = hasFinished(query.data);
  const gaveUp = !runFinished && deadlinePassed;

  // The templates were rewritten server-side, so any cached list of them is stale. This is a
  // cache side effect on an external store rather than something derivable at render, and it
  // must fire once the run reaches a terminal state — not inside queryFn, which React Query
  // treats as pure and calls again on every refetch.
  useEffect(() => {
    if (runFinished) {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
    }
  }, [runFinished, queryClient]);

  return { ...query, gaveUp };
}
